import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Download, Link2, Check, Linkedin, Twitter, Clock, Calendar } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { InsightCard, CategoryBadge, CoverImage } from '../components/insights/InsightCard';
import { WhitePaperGateModal } from '../components/insights/WhitePaperGateModal';
import { setCanonical, setArticleJsonLd, setBreadcrumbJsonLd } from '../lib/seo';
import { getPublicationBySlug, getRelatedPublications, photoFilter } from '../data/insightsConfig';

const serif = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 };
const CONTAINER = 'max-w-[1400px] mx-auto px-6 md:px-10';

// Renders inline emphasis + links: *asterisks* italicize; [label](url) become links.
const renderRich = (text) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((seg, i) => {
    if (!seg) return null;
    const link = seg.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, href] = link;
      const cls = 'text-[#B8864B] underline underline-offset-2 decoration-[#B8864B]/40 hover:decoration-[#B8864B] transition-colors';
      return href.startsWith('/')
        ? <Link key={i} to={href} className={cls}>{label}</Link>
        : <a key={i} href={href} className={cls} target="_blank" rel="noopener noreferrer">{label}</a>;
    }
    if (seg.startsWith('**') && seg.endsWith('**') && seg.length > 4) {
      return <strong key={i} className="font-semibold text-[#2A2018]">{seg.slice(2, -2)}</strong>;
    }
    if (seg.startsWith('*') && seg.endsWith('*') && seg.length > 2) {
      return <em key={i}>{seg.slice(1, -1)}</em>;
    }
    return seg;
  });
};

// ─── Reading progress bar ─────────────────────────────────────────────────────
const ProgressBar = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? Math.min(100, (el.scrollTop / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 h-[3px] z-[60] bg-transparent" data-testid="reading-progress">
      <div
        className="h-full bg-[#B8864B] transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// ─── Static bar chart (no chart library) ──────────────────────────────────────
const BarChart = ({ block }) => {
  const max = Math.max(...block.data.map((d) => d.value), 1);
  return (
    <figure className="my-12 rounded-2xl border border-[#2A2018]/[0.08] bg-[#FBF6EF] p-7 md:p-9" data-testid="insight-chart">
      <figcaption className="mb-7">
        <p className="text-[#1F1812] text-[17px] font-semibold" style={serif}>{block.title}</p>
        {block.unit && <p className="text-[#7A5A2E] text-[12px] tracking-wide uppercase mt-1">{block.unit}</p>}
      </figcaption>
      <div className="space-y-5">
        {block.data.map((d, i) => (
          <div key={i} className="flex items-center gap-4">
            <span className="text-[#5C4F42] text-[13.5px] w-[42%] md:w-[38%] leading-tight">{d.label}</span>
            <div className="flex-1 h-7 bg-[#EDE3D5] rounded-md overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#B8864B] to-[#D4A574] rounded-md flex items-center justify-end pr-2.5 transition-all duration-700 ease-out"
                style={{ width: `${(d.value / max) * 100}%` }}
              >
                <span className="text-white text-[11.5px] font-semibold">{d.value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {block.source && <p className="mt-6 text-[#6B655C] text-[11.5px] italic">Source: {block.source}</p>}
    </figure>
  );
};

// ─── Body block renderer ──────────────────────────────────────────────────────
const Block = ({ block }) => {
  switch (block.type) {
    case 'lead':
      return (
        <p className="text-[#3D3229] text-[20px] md:text-[22px] leading-[1.55] mb-10 font-light" style={{ letterSpacing: '-0.01em' }}>
          {renderRich(block.text)}
        </p>
      );
    case 'h2':
      return (
        <h2
          id={block.id}
          className="text-[#1F1812] scroll-mt-28 mt-14 mb-5"
          style={{ ...serif, fontSize: 'clamp(1.7rem, 2.6vw, 2.3rem)', lineHeight: 1.12, letterSpacing: '-0.02em' }}
        >
          {block.text}
        </h2>
      );
    case 'h3':
      return <h3 className="text-[#1F1812] text-[19px] font-semibold mt-10 mb-3" style={serif}>{block.text}</h3>;
    case 'p':
      return <p className="text-[#4A3F34] text-[17px] leading-[1.75] mb-6 whitespace-pre-line">{renderRich(block.text)}</p>;
    case 'quote':
      return (
        <figure className="my-12 border-l-2 border-[#B8864B] pl-7 md:pl-9">
          <blockquote className="text-[#2A2018] text-[24px] md:text-[28px] leading-[1.35]" style={{ ...serif, fontWeight: 500 }}>
            “{block.text}”
          </blockquote>
          {block.cite && <figcaption className="mt-4 text-[#7A5A2E] text-[13px] tracking-wide uppercase">{block.cite}</figcaption>}
        </figure>
      );
    case 'list':
      return block.ordered ? (
        <ol className="my-7 space-y-3.5 list-none counter-reset">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-4 text-[#4A3F34] text-[16.5px] leading-[1.65]">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2A2018] text-[#F5EDE1] text-[12px] font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
              <span>{it}</span>
            </li>
          ))}
        </ol>
      ) : (
        <ul className="my-7 space-y-3.5">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-4 text-[#4A3F34] text-[16.5px] leading-[1.65]">
              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#B8864B] mt-3" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case 'stats':
      return (
        <div className="my-12 grid sm:grid-cols-3 gap-5" data-testid="insight-stats">
          {block.items.map((s, i) => (
            <div key={i} className="rounded-2xl border border-[#2A2018]/[0.08] bg-[#FBF6EF] p-6">
              <p className="text-[#1F1812]" style={{ ...serif, fontSize: 'clamp(2rem, 3vw, 2.6rem)', fontWeight: 600, lineHeight: 1 }}>{s.value}</p>
              <p className="mt-3 text-[#5C4F42] text-[13.5px] leading-[1.5]">{s.label}</p>
            </div>
          ))}
        </div>
      );
    case 'chart':
      return <BarChart block={block} />;
    case 'callout':
      return (
        <aside className="my-12 rounded-2xl bg-[#2A2018] text-[#F5EDE1] p-7 md:p-9">
          {block.title && <p className="text-[#C9A567] text-[11px] tracking-[0.2em] uppercase mb-3">{block.title}</p>}
          <p className="text-[#E8D9C2] text-[16.5px] leading-[1.7]">{renderRich(block.text)}</p>
        </aside>
      );
    case 'figure':
      return (
        <figure className="my-12">
          <div className="rounded-2xl overflow-hidden border border-[#2A2018]/[0.08]">
            <img src={block.src} alt={block.alt || ''} className="w-full h-auto object-cover" style={{ filter: photoFilter }} loading="lazy" />
          </div>
          {block.caption && <figcaption className="mt-3 text-[#6B655C] text-[13px] italic text-center">{block.caption}</figcaption>}
        </figure>
      );
    case 'cta':
      return (
        <div className="my-10" data-testid="insight-body-cta">
          {block.href.startsWith('/') ? (
            <Link
              to={block.href}
              className="group inline-flex items-center gap-2 bg-[#1F1812] text-white text-[14px] font-medium px-8 py-4 rounded-lg transition-all duration-300 hover:bg-[#0F0D0A] hover:shadow-xl hover:shadow-black/20"
            >
              {block.text}
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          ) : (
            <a
              href={block.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 bg-[#1F1812] text-white text-[14px] font-medium px-8 py-4 rounded-lg transition-all duration-300 hover:bg-[#0F0D0A] hover:shadow-xl hover:shadow-black/20"
            >
              {block.text}
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </a>
          )}
        </div>
      );
    default:
      return null;
  }
};

// ─── Table of contents (sticky) ───────────────────────────────────────────────
const TableOfContents = ({ items }) => {
  const [activeId, setActiveId] = useState(items[0]?.id);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveId(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;
  return (
    <nav className="hidden lg:block sticky top-28 self-start" data-testid="insight-toc">
      <p className="text-[#7A5A2E] text-[11px] tracking-[0.2em] uppercase mb-5">On this page</p>
      <ul className="space-y-3 border-l border-[#2A2018]/[0.1]">
        {items.map((it) => (
          <li key={it.id}>
            <a
              href={`#${it.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(it.id)?.scrollIntoView({ behavior: 'smooth' });
              }}
              className={`block -ml-px pl-4 border-l-2 text-[13.5px] leading-snug transition-colors duration-200 ${
                activeId === it.id
                  ? 'border-[#B8864B] text-[#1F1812] font-medium'
                  : 'border-transparent text-[#7A5A2E] hover:text-[#3D3229]'
              }`}
            >
              {it.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// ─── Share links ──────────────────────────────────────────────────────────────
const ShareLinks = ({ title }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* noop */ }
  };
  const btn = 'inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#2A2018]/[0.14] text-[#5C4F42] hover:text-[#1F1812] hover:border-[#2A2018]/[0.35] transition-colors';
  return (
    <div className="flex items-center gap-2.5" data-testid="insight-share">
      <span className="text-[#7A5A2E] text-[12px] tracking-wide uppercase mr-1">Share</span>
      <a className={btn} data-testid="share-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"><Linkedin size={15} /></a>
      <a className={btn} data-testid="share-twitter" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X"><Twitter size={15} /></a>
      <button className={btn} data-testid="share-copy" onClick={copy} aria-label="Copy link">{copied ? <Check size={15} className="text-[#5B7B6A]" /> : <Link2 size={15} />}</button>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const InsightArticle = () => {
  const { slug } = useParams();
  const pub = getPublicationBySlug(slug);
  const topRef = useRef(null);
  const [showPdfGate, setShowPdfGate] = useState(false);

  const toc = useMemo(
    () => (pub ? pub.body.filter((b) => b.type === 'h2').map((b) => ({ id: b.id, text: b.text })) : []),
    [pub]
  );
  const related = useMemo(() => (pub ? getRelatedPublications(pub, 3) : []), [pub]);

  // Fire-and-forget view ping, used to rank "most-visited" picks on /insights.
  useEffect(() => {
    if (!pub) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: pub.slug }),
    }).catch(() => {});
  }, [pub]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!pub) return;
    const prevTitle = document.title;
    const title = pub.seo?.title || `${pub.title} | Archen™`;
    document.title = title;
    setCanonical(`/insights/${pub.slug}`);

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [key, val] = selector.replace(/meta\[|\]/g, '').split('=');
        el.setAttribute(key, val.replace(/["']/g, ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
      return el;
    };
    const desc = pub.seo?.description || pub.summary;
    const created = [];
    created.push(setMeta('meta[name="description"]', 'content', desc));
    created.push(setMeta('meta[property="og:title"]', 'content', title));
    created.push(setMeta('meta[property="og:description"]', 'content', desc));
    created.push(setMeta('meta[property="og:type"]', 'content', 'article'));
    created.push(setMeta('meta[property="og:image"]', 'content', pub.featuredImage || pub.coverImage || 'https://archen.io/og-image.png'));
    created.push(setMeta('meta[name="twitter:title"]', 'content', title));
    created.push(setMeta('meta[name="twitter:description"]', 'content', desc));

    const removeJsonLd = setArticleJsonLd(pub);
    const removeCrumb = setBreadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Insights', path: '/insights' },
      { name: pub.title, path: `/insights/${pub.slug}` },
    ]);

    return () => { document.title = prevTitle; removeJsonLd(); removeCrumb(); };
  }, [pub]);

  if (!pub) return <Navigate to="/insights" replace />;

  const isPaper = pub.category === 'White Paper' || pub.category === 'Research';

  return (
    <div className="bg-[#FDF8F3] min-h-screen" data-testid="insight-article-page" style={{ fontFamily: "'Inter', system-ui, sans-serif" }} ref={topRef}>
      <ProgressBar />
      <Header />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-10 md:pb-14">
        <div className="max-w-[1120px] mx-auto px-6 md:px-10">
          <Link to="/insights" data-testid="insight-back" className="inline-flex items-center gap-2 text-[#7A5A2E] hover:text-[#3D3229] text-[13px] font-medium mb-9 transition-colors">
            <ArrowLeft size={15} /> All Insights
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <CategoryBadge category={pub.category} />
            {pub.subtitle && <span className="text-[#7A5A2E] text-[13px] tracking-[0.14em] uppercase">{pub.subtitle}</span>}
          </div>
          <h1
            className="text-[#1F1812]"
            style={{ ...serif, fontSize: 'clamp(2.4rem, 5vw, 4rem)', lineHeight: 1.04, letterSpacing: '-0.03em', textWrap: 'balance' }}
            data-testid="insight-article-title"
          >
            {pub.title}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[#5C4F42] text-[14px]">
            <span className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#B8864B] to-[#A07040] flex items-center justify-center text-white text-[12px] font-semibold">{pub.author[0]}</span>
              <span className="font-medium text-[#3D3229]">{pub.author}</span>
              {pub.authorRole && <span className="text-[#7A5A2E]">· {pub.authorRole}</span>}
            </span>
            <span className="flex items-center gap-1.5 text-[#7A5A2E]"><Calendar size={14} /> {pub.publishDate}</span>
            <span className="flex items-center gap-1.5 text-[#7A5A2E]"><Clock size={14} /> {pub.readingTime}</span>
          </div>
        </div>
      </section>

      {/* Cover image */}
      <section className="pb-4 md:pb-10">
        <div className="max-w-[1120px] mx-auto px-6 md:px-10">
          <div className="rounded-[24px] overflow-hidden border border-[#2A2018]/[0.08] aspect-[16/8]">
            <CoverImage src={pub.featuredImage || pub.coverImage} alt={pub.title} imgClassName="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Body + TOC */}
      <section className="py-10 md:py-16">
        <div className="max-w-[1120px] mx-auto px-6 md:px-10">
          <div className={toc.length > 0 ? 'grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-16' : ''}>
            {toc.length > 0 && <TableOfContents items={toc} />}
            <article className="min-w-0 max-w-[720px] mx-auto lg:mx-0" data-testid="insight-article-body">
              {pub.body.map((block, i) => <Block key={i} block={block} />)}

              {/* Tags + share + PDF */}
              <div className="mt-14 pt-8 border-t border-[#2A2018]/[0.1] flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                <div className="flex flex-wrap gap-2">
                  {(pub.tags || []).map((t) => (
                    <span key={t} className="text-[#5C4F42] text-[12px] px-3 py-1.5 rounded-full bg-[#EDE3D5]">{t}</span>
                  ))}
                </div>
                <div className="flex items-center gap-5">
                  {pub.pdf && (
                    <button
                      type="button"
                      onClick={() => setShowPdfGate(true)}
                      data-testid="insight-download-pdf"
                      className="inline-flex items-center gap-2 text-[#1F1812] text-[13.5px] font-medium hover:text-[#B8864B] transition-colors"
                    >
                      <Download size={15} /> Download PDF
                    </button>
                  )}
                  <ShareLinks title={pub.title} />
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1120px] mx-auto px-6 md:px-10">
          <div className="rounded-[28px] bg-[#2A2018] text-[#F5EDE1] p-10 md:p-16 text-center">
            <p className="text-[#C9A567] text-[11px] tracking-[0.22em] uppercase mb-6">{isPaper ? 'Continue the work' : 'See it in practice'}</p>
            <h2 className="text-white mx-auto max-w-[640px]" style={{ ...serif, fontSize: 'clamp(1.9rem, 3.4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              Stop paying interest on decisions you already made.
            </h2>
            <p className="mt-6 text-[#D6C6AE] text-[16px] leading-[1.7] max-w-[540px] mx-auto">
              Archen™ turns every project decision into a searchable record, automatically, so your studio never re-litigates the same choice twice.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <a href="https://calendly.com/hello-archen" target="_blank" rel="noopener noreferrer" data-testid="insight-cta-demo" className="group inline-flex items-center gap-2 bg-[#F5EDE1] text-[#1F1812] hover:bg-white text-[14px] font-semibold px-7 py-3.5 rounded-lg transition-all duration-300">
                Talk to the founders <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
              </a>
              <Link to="/foundingdesigner" data-testid="insight-cta-apply" className="inline-flex items-center gap-2 border border-white/25 text-[#E8D9C2] hover:text-white hover:border-white/50 text-[14px] font-medium px-7 py-3.5 rounded-lg transition-colors">
                Founding Designer Program
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="pb-24 md:pb-32">
          <div className={CONTAINER}>
            <div className="flex items-end justify-between mb-10 border-t border-[#2A2018]/[0.08] pt-10">
              <h2 className="text-[#1F1812]" style={{ ...serif, fontSize: 'clamp(1.6rem, 2.6vw, 2.2rem)', letterSpacing: '-0.02em' }}>Keep reading</h2>
              <Link to="/insights" data-testid="insight-related-all" className="group inline-flex items-center gap-2 text-[#1F1812] text-[14px] font-medium">
                All Insights <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="insight-related-grid">
              {related.map((p) => <InsightCard key={p.id} pub={p} />)}
            </div>
          </div>
        </section>
      )}

      <Footer />
      {showPdfGate && <WhitePaperGateModal pub={pub} onClose={() => setShowPdfGate(false)} />}
    </div>
  );
};

export default InsightArticle;
